import React from 'react';
import { ChevronRight } from 'lucide-react';
import { focusRing } from './utils/a11y';

/**
 * Breadcrumbs Component
 * 
 * Navigation breadcrumbs for hierarchical navigation.
 * 
 * @example
 * ```tsx
 * <Breadcrumbs>
 *   <Breadcrumbs.Item href="/">Home</Breadcrumbs.Item>
 *   <Breadcrumbs.Item href="/settings">Settings</Breadcrumbs.Item>
 *   <Breadcrumbs.Item current>Database</Breadcrumbs.Item>
 * </Breadcrumbs>
 * ```
 */

export interface BreadcrumbsProps {
  children?: React.ReactNode;
  /** Separator icon size */
  separatorSize?: number;
  /** Additional className */
  className?: string;
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  children,
  separatorSize = 16,
  className = '',
}) => {
  const items = React.Children.toArray(children);

  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="flex items-center gap-2 flex-wrap">
        {items.map((child, index) => (
          <React.Fragment key={index}>
            {child}
            {index < items.length - 1 && (
              <li aria-hidden="true">
                <ChevronRight
                  size={separatorSize}
                  className="text-gray-400 dark:text-gray-600"
                />
              </li>
            )}
          </React.Fragment>
        ))}
      </ol>
    </nav>
  );
};

Breadcrumbs.displayName = 'Breadcrumbs';

/**
 * Breadcrumb Item
 */
export interface BreadcrumbItemProps {
  children?: React.ReactNode;
  /** Link href (if not current) */
  href?: string;
  /** Is current page */
  current?: boolean;
  /** Click handler */
  onClick?: () => void;
  /** Additional className */
  className?: string;
}

const BreadcrumbItem: React.FC<BreadcrumbItemProps> = ({
  children,
  href,
  current = false,
  onClick,
  className = '',
}) => {
  const baseStyles = `text-sm font-medium transition-colors ${focusRing}`;

  const styles = current
    ? `${baseStyles} text-gray-900 dark:text-dark-text cursor-default`
    : `${baseStyles} text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer`;

  if (current) {
    return (
      <li aria-current="page" className={`${styles} ${className}`}>
        {children}
      </li>
    );
  }

  if (href) {
    return (
      <li>
        <a href={href} onClick={onClick} className={`${styles} ${className}`}>
          {children}
        </a>
      </li>
    );
  }

  return (
    <li>
      <button onClick={onClick} className={`${styles} ${className}`}>
        {children}
      </button>
    </li>
  );
};

BreadcrumbItem.displayName = 'BreadcrumbItem';

// Compound component
export default Object.assign(Breadcrumbs, {
  Item: BreadcrumbItem,
});
